<template>
  <div>
    <b-card no-body>
      <b-card-body>
        <validation-observer ref="refFormObserver">
          <b-form>
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h5 class="mb-0">{{ $t("dmp.deviceSetup.title") }}</h5>
              <div class="d-flex gap-1">
                <b-button variant="primary" size="sm" @click="addNewCardFormat" class="text-capitalize">
                  <feather-icon icon="CreditCardIcon" size="14" class="mr-25" />
                  {{ $t("dmp.deviceSetup.addCardFormat") }}
                </b-button>
                <b-button variant="primary" size="sm" @click="addNewDevice"
                  :disabled="isLoadingDevices || isSavingDevice" class="text-capitalize">
                  <feather-icon icon="PlusIcon" size="14" class="mr-25" />
                  {{ $t("dmp.deviceSetup.addDevice") }}
                </b-button>
              </div>
            </div>

            <div v-if="isLoadingDevices" class="text-center py-4">
              <b-spinner variant="primary" :label="$t('dmp.deviceSetup.loadingDevices')"></b-spinner>
              <p class="mt-2 mb-0">{{ $t("dmp.deviceSetup.loadingDevices") }}</p>
            </div>

            <b-row v-if="!isLoadingDevices">
              <b-col cols="4" class="table-column">
                <!-- Devices Table -->
                <b-table :items="devices" :fields="deviceTableFields" responsive striped hover show-empty
                  :empty-text="$t('dmp.deviceSetup.noDevicesConfigured')" selectable select-mode="single"
                  @row-selected="onRowSelected" sort-by="number" :sort-desc="false" class="mb-0">
                  <template #cell(number)="data">
                    {{ data.item.number }}
                  </template>

                  <template #cell(name)="data">
                    {{ data.item.name }}
                  </template>
                </b-table>
              </b-col>

              <b-col cols="8" class="details-column">
                <!-- Selected Device Details Card -->
                <b-card v-if="selectedDevice" no-body>
                  <b-card-header class="mb-0card-header-custom pt-0">
                    <h6 class="mb-0">{{ $t("dmp.deviceSetup.deviceDetails") }}</h6>
                  </b-card-header>
                  <b-card-body>
                    <validation-observer ref="deviceFormObserver">
                      <b-form @submit.prevent="saveDevice">
                        <!-- Tabs for General and 734 Options -->
                        <b-tabs v-model="activeTab" pills class="device-details-tabs">
                          <!-- General Tab -->
                          <b-tab :title="$t('dmp.deviceSetup.general')" active>
                            <general-tab :device-form="deviceForm" :selected-device="selectedDevice"
                              :device-type-options="allowedDeviceTypeOptions"
                              :communication-type-options="communicationTypeOptions"
                              :supervision-time-options="supervisionTimeOptions" :is-saving="isSavingDevice"
                              :is-deleting="isDeletingDevice" :is-editing="isEditingDevice"
                              :get-validation-state="getValidationState" @update:device-form="deviceForm = $event" />
                          </b-tab>

                          <!-- 734 Options Tab -->
                          <b-tab :title="$t('dmp.deviceSetup.options734')">
                            <options734-tab :device-form="deviceForm" :selected-device="selectedDevice"
                              :is-door="show734OptionsTab" @update:device-form="deviceForm = $event" />
                          </b-tab>
                        </b-tabs>

                        <!-- Action Buttons -->
                        <b-row class="mt-3">
                          <b-col cols="12" class="d-flex justify-content-end">
                            <b-button v-if="isEditingDevice" variant="outline-danger" size="sm" class="mr-1"
                              @click="deleteDevice" :disabled="isSavingDevice || isDeletingDevice">
                              <b-spinner v-if="isDeletingDevice" small class="mr-25" />
                              <feather-icon v-else icon="TrashIcon" size="14" class="mr-25" />
                              <span>{{ $t("actions.delete") }}</span>
                            </b-button>
                            <b-button variant="primary" size="sm" type="submit"
                              :disabled="isSavingDevice || isDeletingDevice" @click="saveDevice">
                              <b-spinner v-if="isSavingDevice" small class="mr-25" />
                              <feather-icon v-else icon="SaveIcon" size="14" class="mr-25" />
                              <span>{{
                                isSavingDevice ? $t("actions.saving") : $t("actions.save")
                                }}</span>
                            </b-button>
                          </b-col>
                        </b-row>
                      </b-form>
                    </validation-observer>
                  </b-card-body>
                </b-card>
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>
      </b-card-body>
    </b-card>

    <card-format-modal :is-active.sync="isCardFormatModalActive" :uuid="uuid" :panel-id="panelId"
      :card-formats-data="cardFormatsData" :source="source" @refetch-data="$emit('operation-completed')"
      @template-update="$emit('template-update', $event)"/>
  </div>
</template>

<script>
import {
  ref,
  computed,
  watch,
  onMounted,
  getCurrentInstance,
} from "@vue/composition-api";
import { ValidationObserver, ValidationProvider } from "vee-validate";

import { useUtils as useI18nUtils } from "@core/libs/i18n";
import { useToast } from "vue-toastification/composition";
import formValidation from "@core/comp-functions/forms/form-validation";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import store from "@/store";
import Swal from "sweetalert2";
import GeneralTab from "./GeneralTab.vue";
import CardFormatModal from "./CardFormat.vue";
import Options734Tab from "./Options734Tab.vue";
import {
  transformDeviceFromBackend,
  transformDeviceToBackend,
  transformDeviceToTemplate,
} from "./deviceTransformers";
import { useTemplateMode } from "../useTemplateMode";

export default {
  name: "ConfigDeviceSetup",
  components: {
    ValidationObserver,
    ValidationProvider,
    GeneralTab,
    Options734Tab,
    CardFormatModal,
  },
  props: {
    uuid: {
      type: String,
      required: true,
    },
    panelId: {
      type: [Number, String],
      required: true,
    },
    deviceSetupData: {
      type: Array,
      default: () => [],
    },
    cardFormatsData: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    source: {
      type: String,
      default: "default",
    },
  },
  setup(props, { emit }) {
    const { isTemplateMode, emitTemplateUpdate, nextSequentialId } = useTemplateMode(props, emit);
    const isCardFormatModalActive = ref(false);

    const addNewCardFormat = () => {
      isCardFormatModalActive.value = true;
    };
    const vm = getCurrentInstance();
    const proxy = vm.root.proxy;

    const { t } = useI18nUtils();
    const toast = useToast();
    const { refFormObserver, getValidationState } = formValidation();

    const devices = ref([]);
    const selectedDevice = ref(null);
    const isLoadingDevices = ref(false);
    const isSavingDevice = ref(false);
    const isDeletingDevice = ref(false);
    const activeTab = ref(0);

    const deviceFormObserver = ref(null);
    const isEditingDevice = ref(false);
    const editingDeviceIndex = ref(-1);

    // Default values for device form
    const getDefaultDeviceForm = () => ({
      deviceNumber: "",
      deviceName: "",
      deviceType: "",
      deviceCommType: "K",
      serialNumber: "",
      supervisionTime: "3",
      accessAreas: "",
      egressAreas: "",
      displayAreas: "1-32",
      strikeTime: "5",
      strikeDelay: "0",
      fireExit: "N",
      autoForceArm: "N",
      publicDoor: "N",
      outputGroup: "N",
      doorRealTimeStatus: "N",
      override: "N",
      sendDoorForced: "N",
      program734Options: "N",
      extendedConfig: {
        deviceNumber: "",
        activateZone2Bypass: "N",
        relockOnZone2Change: "N",
        zone2BypassTime: "40",
        activateZone3RequestExit: "N",
        requestExitStrikeTime: "5",
        activateOnboardSpeaker: "Y",
        noCommWithPanel: "0",
        cardOption: "1",
        siteCodeLength: "8",
        wiegandCodeLength: "26",
        userCodePosition: "9",
        siteCodePosition: "1",
        userCodeLength: "16",
        requireSiteCode: "N",
        siteCode1: "",
        siteCode2: "",
        siteCode3: "",
        siteCode4: "",
        siteCode5: "",
        siteCode6: "",
        siteCode7: "",
        siteCode8: "",
        numUserCodeDigits: "5",
      },
    });

    const deviceForm = ref(getDefaultDeviceForm());

    const deviceTableFields = computed(() => [
      {
        key: "number",
        label: t("dmp.deviceSetup.number"),
        sortable: true,
        thStyle: { width: "50px" },
      },
      { key: "name", label: t("dmp.deviceSetup.name"), sortable: true },
    ]);

    const deviceTypeOptions = computed(() => [
      { text: "", value: "", disabled: true },
      { text: "Door", value: "1" },
      { text: "Keypad", value: "3" },
      { text: "Zone Expander", value: "4" },
      { text: "Video Doorbell", value: "5" },
      { text: "Fire", value: "6" },
    ]);

    // Números de dispositivo que SOLO permiten Door
    const DOOR_ONLY_NUMBERS = new Set([
      501, 505, 509, 513, 517, 521, 525, 529, 533, 537, 541, 545, 549, 553, 557, 561,
      601, 605, 609, 613, 617, 621, 625, 629, 633, 637, 641, 645, 649, 653, 657, 661,
      701, 705, 709, 713, 717, 721, 725, 729, 733, 737, 741, 745, 749, 753, 757, 761,
      801, 805, 809, 813, 817, 821, 825, 829, 833, 837, 841, 845, 849, 853, 857, 861,
      901, 905, 909, 913, 917, 921, 925, 929, 933, 937, 941, 945, 949, 953, 957, 961,
    ]);

    const allowedDeviceTypeOptions = computed(() => {
      const num = parseInt(deviceForm.value.deviceNumber, 10);
      if (!num || isNaN(num)) return deviceTypeOptions.value;

      if (num >= 1 && num <= 16) {
        // 1-16: Todos los tipos
        return deviceTypeOptions.value;
      }
      if (DOOR_ONLY_NUMBERS.has(num)) {
        // Solo DOOR
        return deviceTypeOptions.value.filter((o) => o.value === "1");
      }
      return deviceTypeOptions.value;
    });

    const communicationTypeOptions = computed(() => [
      { text: "Keypad Bus", value: "K" },
      { text: "Wireless", value: "W" },
      { text: "Network", value: "N" },
    ]);

    const supervisionTimeOptions = computed(() => [
      { text: "None Timer", value: "0" },
      { text: "60", value: "2" },
      { text: "240", value: "3" },
    ]);

    // Solo se muestra si el Device Type es DOOR (1)
    const show734OptionsTab = computed(() => {
      return deviceForm.value.deviceType === "1";
    });

    const getDeviceTypeLabel = (type) => {
      switch (type) {
        case "0":
          return "None";
        case "1":
          return "Door";
        case "2":
          return "Reader";
        case "3":
          return "Keypad";
        case "4":
          return "Zone Expander";
        case "5":
          return "Video Doorbell";
        case "6":
          return "Fire";
        default:
          return "None";
      }
    };

    const loadDevices = () => {
      try {
        const backendDevices = Array.isArray(props.deviceSetupData)
          ? props.deviceSetupData
          : [];

        devices.value = backendDevices.map((device) => ({
          number: device.deviceNumber,
          name: device.deviceName || "",
          type: device.deviceType,
          _raw: transformDeviceFromBackend(device), // Transform from backend format
        }));
      } catch (error) {
        console.error("Error loading devices:", error);
        devices.value = [];
      }
    };

    const addNewDevice = () => {
      const existingNumbers = new Set(
        devices.value.map((d) => parseInt(d.number)).filter((n) => !isNaN(n))
      );

      // Pool de números válidos: 1-16 primero, luego DOOR_ONLY_NUMBERS
      const validPool = [
        ...Array.from({ length: 16 }, (_, i) => i + 1),
        ...DOOR_ONLY_NUMBERS,
      ];
      const nextDeviceNumber = validPool.find((n) => !existingNumbers.has(n));

      if (nextDeviceNumber === undefined) {
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("warning"),
            text: proxy.$t("dmp.deviceSetup.maxDevicesReached"),
            icon: "AlertTriangleIcon",
            variant: "warning",
          },
        });
        return;
      }

      const isDoorOnly = DOOR_ONLY_NUMBERS.has(nextDeviceNumber);
      const defaultType = isDoorOnly ? "1" : "";

      isEditingDevice.value = false;
      editingDeviceIndex.value = -1;
      activeTab.value = 0;

      // Crear dispositivo temporal con valores por defecto
      selectedDevice.value = {
        number: nextDeviceNumber.toString(),
        name: `Device ${nextDeviceNumber}`,
        type: defaultType,
        _raw: null,
      };

      // Resetear formulario con valores por defecto
      const defaultDevice = getDefaultDeviceForm();
      defaultDevice.deviceNumber = nextDeviceNumber.toString();
      defaultDevice.deviceName = `Device ${nextDeviceNumber}`;
      defaultDevice.deviceType = defaultType;
      defaultDevice.extendedConfig.deviceNumber = nextDeviceNumber.toString();

      deviceForm.value = defaultDevice;

      if (deviceFormObserver.value) {
        deviceFormObserver.value.reset();
      }
    };

    const onRowSelected = (items) => {
      if (items.length > 0) {
        const selected = items[0];
        isEditingDevice.value = true;
        editingDeviceIndex.value = devices.value.findIndex(
          (d) => d.number === selected.number
        );
        selectedDevice.value = selected;
        activeTab.value = 0;

        if (selected._raw) {
          const defaultExtendedConfig = getDefaultDeviceForm().extendedConfig;
          deviceForm.value = {
            ...selected._raw,
            extendedConfig: selected._raw.extendedConfig || {
              ...defaultExtendedConfig,
              deviceNumber: selected._raw.deviceNumber,
            },
          };
        }
      } else {
        selectedDevice.value = null;
        isEditingDevice.value = false;
      }
    };

    const deleteDevice = async () => {
      if (!isEditingDevice.value || !selectedDevice.value) {
        return;
      }

      const result = await Swal.fire({
        title: proxy.$t("messages.delete.title"),
        text: proxy.$t("messages.delete.body"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: proxy.$t("actions.delete"),
        cancelButtonText: proxy.$t("actions.cancel"),
        customClass: {
          confirmButton: "btn btn-primary text-capitalize",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      });

      if (!result.isConfirmed) {
        return;
      }

      if (isTemplateMode.value) {
        const list = (props.deviceSetupData || []).filter(
          (d) =>
            parseInt(d.deviceNumber, 10) !==
            parseInt(selectedDevice.value.number, 10)
        );
        emitTemplateUpdate("devices", list);
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.success"),
            text: proxy.$t("dmp.deviceSetup.deviceDeleted"),
            icon: "CheckIcon",
            variant: "success",
          },
        });
        selectedDevice.value = null;
        isEditingDevice.value = false;
        return;
      }

      isDeletingDevice.value = true;
      store.commit("dmp_panels/setOperationActive", true);

      try {
        const response = await store.dispatch("dmp_panels/deleteDeviceSetup", {
          uuid: props.uuid,
          deviceNumber: selectedDevice.value.number,
        });

        if (response.success) {
          emit("operation-completed");

          toast({
            component: ToastificationContent,
            props: {
              title: proxy.$t("dmp.common.success"),
              text: proxy.$t("dmp.deviceSetup.deviceDeleted"),
              icon: "CheckIcon",
              variant: "success",
            },
          });

          selectedDevice.value = null;
          isEditingDevice.value = false;
        } else {
          throw new Error(response.message || "Failed to delete device");
        }
      } catch (error) {
        console.error("Error deleting device:", error);
        emit("operation-error", error);
        const errorMessage = error.response?.data?.message || error.message;
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.error"),
            text: errorMessage || proxy.$t("dmp.deviceSetup.errorDeletingDevice"),
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        isDeletingDevice.value = false;
        store.commit("dmp_panels/setOperationActive", false);
      }
    };

    const saveDevice = async () => {
      const isValid = await deviceFormObserver.value.validate();
      if (!isValid) {
        return;
      }

      if (isTemplateMode.value) {
        const list = [...(props.deviceSetupData || [])];
        const idx = list.findIndex(
          (d) =>
            parseInt(d.deviceNumber, 10) ===
            parseInt(deviceForm.value.deviceNumber, 10)
        );
        const original = idx !== -1 ? list[idx] : {};
        const item = transformDeviceToTemplate(deviceForm.value, original);
        if (idx !== -1) {
          item.id = original.id != null ? original.id : nextSequentialId(list);
          list.splice(idx, 1, item);
        } else {
          item.id = nextSequentialId(list);
          list.push(item);
        }

        emitTemplateUpdate("devices", list);
        toast({
          component: ToastificationContent,
          props: {
            title: isEditingDevice.value
              ? proxy.$t("dmp.deviceSetup.deviceUpdated")
              : proxy.$t("dmp.deviceSetup.deviceCreated"),
            icon: "CheckIcon",
            variant: "success",
          },
        });
        selectedDevice.value = null;
        isEditingDevice.value = false;
        return;
      }

      isSavingDevice.value = true;
      store.commit("dmp_panels/setOperationActive", true);

      try {
        // Transform device data to backend format
        const deviceData = transformDeviceToBackend(deviceForm.value);

        const response = await store.dispatch("dmp_panels/setDeviceSetup", {
          uuid: props.uuid,
          deviceData: deviceData,
        });

        if (response.success) {
          emit("operation-completed");

          toast({
            component: ToastificationContent,
            props: {
              title: isEditingDevice.value
                ? proxy.$t("dmp.deviceSetup.deviceUpdated")
                : proxy.$t("dmp.deviceSetup.deviceCreated"),
              icon: "CheckIcon",
              variant: "success",
            },
          });

          selectedDevice.value = null;
          isEditingDevice.value = false;
        } else {
          throw new Error(response.message || "Failed to save device");
        }
      } catch (error) {
        console.error("Error saving device:", error);
        emit("operation-error", error);
        const errorMessage = error.response?.data?.message || error.message;
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.error"),
            text: errorMessage || proxy.$t("dmp.deviceSetup.errorSavingDevice"),
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        store.commit("dmp_panels/setOperationActive", false);
        isSavingDevice.value = false;
      }
    };

    const resetDeviceForm = () => {
      deviceForm.value = getDefaultDeviceForm();

      if (deviceFormObserver.value) {
        deviceFormObserver.value.reset();
      }

      selectedDevice.value = null;
    };

    watch(
      () => props.deviceSetupData,
      () => { loadDevices(); },
      { immediate: true, deep: true }
    );

    watch(
      () => props.cardFormatsData,
      () => { loadDevices(); },
      { deep: true }
    );

    watch(
      () => deviceForm.value.deviceNumber,
      (newNum) => {
        const num = parseInt(newNum, 10);
        if (!num || isNaN(num)) return;

        if (DOOR_ONLY_NUMBERS.has(num)) {
          if (deviceForm.value.deviceType !== "1") {
            deviceForm.value = { ...deviceForm.value, deviceType: "1" };
          }
        }
      }
    );

    onMounted(() => {
      loadDevices();
    });

    return {
      // State variables
      devices,
      selectedDevice,
      isLoadingDevices,
      isSavingDevice,
      isDeletingDevice,
      isEditingDevice,
      editingDeviceIndex,
      activeTab,
      deviceForm,
      deviceFormObserver,

      // Computed properties
      deviceTableFields,
      deviceTypeOptions,
      allowedDeviceTypeOptions,
      communicationTypeOptions,
      supervisionTimeOptions,
      show734OptionsTab,

      // Methods
      getDeviceTypeLabel,
      loadDevices,
      addNewDevice,
      onRowSelected,
      deleteDevice,
      saveDevice,
      resetDeviceForm,

      // Form validation
      refFormObserver,
      getValidationState,
      isCardFormatModalActive,
      addNewCardFormat,
      // Utilities
      t,
      toast,
    };
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/variables/_variables.scss";

.card-header-custom {
  background-color: transparent;
}

.table-column,
.details-column {
  transition: all 0.3s ease-in-out;
}

.details-column {
  .card {
    position: sticky;
    top: 5rem;
  }
}

.device-details-tabs {
  ::v-deep .nav-pills {
    .nav-link {
      padding: 0.5rem 1rem;
    }
  }
}

.dark-layout {
  .card-header-custom {
    background-color: transparent !important;
  }
}

.gap-1 {
  gap: 0.25rem;
}

.mb-75 {
  margin-bottom: 0.75rem;
}

.font-weight-bold {
  font-weight: 600;
}
</style>
